import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { SealCheck, MagnifyingGlass, ShieldCheck, Warning, X } from "@phosphor-icons/react";

function fmtType(t) {
    if (t === "yes_no") return "Yes / No";
    if (t === "for_against") return "For / Against";
    if (t === "text_response") return "Text answer";
    if (t === "rating") return "Rating 1–10";
    return "Multi-choice";
}

export default function ReceiptVerifier({ compact = false, initialHash = "" }) {
    const [query, setQuery] = useState(initialHash);
    const [searching, setSearching] = useState(false);
    const [match, setMatch] = useState(null); // result | "notfound" | null
    const rootRef = useRef(null);

    const lookup = async (raw) => {
        const q = raw.trim().toLowerCase();
        if (!q) return;
        setSearching(true);
        setMatch(null);
        try {
            const { data } = await api.get(`/blockchain/block/${encodeURIComponent(q)}`);
            setMatch(data);
        } catch {
            setMatch("notfound");
        }
        setSearching(false);
    };
    const submit = (e) => { e?.preventDefault(); lookup(query); };

    useEffect(() => {
        if (!initialHash) return;
        lookup(initialHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialHash]);
    useEffect(() => {
        if (!initialHash || !match || query.trim().toLowerCase() !== initialHash.toLowerCase()) return undefined;
        const t = setTimeout(() => requestAnimationFrame(() => {
            const el = rootRef.current?.querySelector('[data-testid="receipt-found"], [data-testid="receipt-notfound"]') || rootRef.current;
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }), 400);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [match]);
    const clear = () => { setMatch(null); setQuery(""); };

    return (
        <Card ref={rootRef} className={`relative overflow-hidden ${compact ? "p-3" : "p-5"} bg-gradient-to-b from-white to-civic-50/30 border-slate-200/80 shadow-md hover:shadow-lg transition-shadow ring-1 ring-slate-200/60`} data-testid="receipt-verifier">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-civic-400 to-civic-600" aria-hidden="true" />
            <div className="flex items-start gap-2">
                <div className={`${compact ? "h-7 w-7" : "h-9 w-9"} grid place-items-center rounded-md bg-civic-50 border border-civic-100 shrink-0`}>
                    <SealCheck size={compact ? 14 : 18} weight="duotone" className="text-civic-600" />
                </div>
                <div className="min-w-0">
                    <div className="overline text-civic-600 text-[10px]">Verify your receipt</div>
                    <h2 className={`font-heading font-bold ${compact ? "text-sm" : "text-base"} text-slate-900 mt-0.5 leading-tight`}>Paste your block hash</h2>
                    {!compact && <p className="text-xs text-slate-500 mt-0.5">Anonymous lookup — only the choices on the block are shown, not who cast them.</p>}
                </div>
            </div>

            <form onSubmit={submit} className={`${compact ? "mt-2" : "mt-3"} flex flex-wrap gap-2 items-center`} data-testid="receipt-verify-form">
                <div className="relative flex-1 min-w-[180px]">
                    <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={compact ? "64-char hash" : "Paste your 64-character block hash"}
                        className={`pl-7 font-mono ${compact ? "h-8 text-[11px]" : "h-9 text-xs"}`}
                        data-testid="receipt-hash-input"
                    />
                </div>
                <Button type="submit" disabled={searching || !query.trim()} className={`${compact ? "h-8 text-[11px] px-3" : "h-9 text-xs"} bg-civic-600 hover:bg-civic-700 text-white`} data-testid="receipt-verify-btn">
                    {searching ? "…" : "Verify"}
                </Button>
                {match && (
                    <Button type="button" variant="ghost" onClick={clear} className={`${compact ? "h-8 w-8 p-0" : "h-9"}`} data-testid="receipt-clear-btn">
                        <X size={14} />
                    </Button>
                )}
            </form>

            <AnimatePresence>
                {match === "notfound" && (
                    <motion.div
                        key="notfound"
                        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="mt-3 p-3 rounded-md border border-red-200 bg-red-50 flex items-start gap-2"
                        data-testid="receipt-notfound"
                    >
                        <Warning size={16} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
                        <div className="text-xs">
                            <div className="font-semibold text-red-800">No block with that hash</div>
                            <div className="text-red-700">Either the hash was mistyped or the receipt does not belong to this chain.</div>
                        </div>
                    </motion.div>
                )}

                {match && match !== "notfound" && (
                    <motion.div
                        key="found"
                        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className={`mt-3 p-3 rounded-md border ${match.anchored ? "border-emerald-200 bg-emerald-50" : "border-red-300 bg-red-50"}`}
                        data-testid="receipt-found"
                    >
                        <div className="flex items-center gap-2">
                            {match.anchored
                                ? <ShieldCheck size={18} weight="fill" className="text-emerald-600" />
                                : <Warning size={18} weight="fill" className="text-red-700" />}
                            <div className={`font-heading font-bold text-sm ${match.anchored ? "text-emerald-800" : "text-red-800"}`}>
                                {match.anchored
                                    ? `Anchored on chain at #${match.block.index}${match.election_title ? ` · ${match.election_title}` : ""}`
                                    : `Block #${match.block.index} hash mismatch — record may be tampered.`}
                            </div>
                        </div>

                        {match.anchored && (
                            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <div className="overline text-slate-500">Block</div>
                                    <div className="hash-mono text-slate-900 mt-0.5">#{match.block.index}</div>
                                </div>
                                <div>
                                    <div className="overline text-slate-500">Sealed at</div>
                                    <div className="hash-mono text-slate-900 mt-0.5">{match.block.timestamp?.slice(0, 19).replace("T", " ")}</div>
                                </div>
                            </div>
                        )}

                        {match.anchored && match.block?.data?.type === "vote" && (
                            <div className="mt-3 rounded-md bg-white border border-slate-200 p-3" data-testid="receipt-choices">
                                <div className="overline text-slate-500 mb-2">Your choices on this block</div>

                                {match.candidate_name ? (
                                    <div className="mb-2 flex items-baseline gap-2 flex-wrap" data-testid="receipt-candidate-row">
                                        <span className="text-[10px] uppercase tracking-wider text-slate-500 w-24 shrink-0">Candidate</span>
                                        <span className="font-semibold text-slate-900 text-sm">{match.candidate_name}</span>
                                    </div>
                                ) : null}

                                {(match.question_choices || []).length > 0 ? (
                                    <div className="space-y-1.5" data-testid="receipt-questions">
                                        {match.question_choices.map((q, i) => (
                                            <div key={i} className="flex items-baseline gap-2 flex-wrap" data-testid={`receipt-question-${i}`}>
                                                <span className="text-[10px] uppercase tracking-wider text-slate-500 w-24 shrink-0">{fmtType(q.type)}</span>
                                                <span className="text-xs text-slate-700 flex-1 min-w-0">
                                                    <span className="font-semibold text-slate-900">{q.title}</span>
                                                    <span className="mx-1.5 text-slate-400">→</span>
                                                    <span className="font-semibold text-civic-700 hash-mono">{Array.isArray(q.choice) ? q.choice.join(", ") : q.choice}</span>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}

                                {!match.candidate_name && (match.question_choices || []).length === 0 && (
                                    <div className="text-xs text-slate-500 italic">This block contains no candidate or question selections.</div>
                                )}
                            </div>
                        )}

                        <div className="mt-3 hash-mono text-[10px] text-slate-600 break-all bg-white border border-slate-200 rounded-sm p-2">
                            {match.block.hash}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </Card>
    );
}
